import { useState, useCallback } from 'react';
import { toast } from 'react-toastify';
import { getValidationStatusesForce, updateValidationStatusForce } from 'api/FilterApi';

const REPORT_TYPE_MAPPING = {
  ha: 'ha',
  ha_report: 'ha',
  nts: 'nts',
  nts_report: 'nts',
  eore: 'eore',
  eore_report: 'eore',
  clearance: 'clearance-task',
  daily: 'daily-reports',
  weekly: 'weekly-reports'
};

const normalizeStatusLabel = (value, label) => {
  const labelMappings = {
    'draft': 'Draft',
    'draft_rejected': 'Draft (rejected)',
    'validated': 'Validated',
    'imsma_rejected': 'IMSMA Rejected'
  };

  if (label && label.includes('val_')) {
    const rawValueMatch = label?.match(/\((\w+)\)/);
    if (rawValueMatch) {
      const rawValue = rawValueMatch[1];

      const valMatch = rawValue.match(/^val_(\d+)_(\d+)$/);
      if (valMatch) {
        const [, step, total] = valMatch;
        return `Validation (${step}/${total})`;
      }

      if (labelMappings[rawValue]) {
        return labelMappings[rawValue];
      }
    }
  }

  if (labelMappings[value]) {
    return labelMappings[value];
  }

  const valMatch = value?.match(/^val_(\d+)_(\d+)$/);
  if (valMatch) {
    const [, step, total] = valMatch;
    return `Validation (${step}/${total})`;
  }

  return label;
};

export const useValidationStatus = () => {
  const [loading, setLoading] = useState(false);
  const [statusOptions, setStatusOptions] = useState([]);

  const getReportTypeForAPI = (reportType) => {
    return REPORT_TYPE_MAPPING[reportType] || reportType;
  };

    const fetchValidationStatuses = useCallback(async (reportType, reportId) => {
    setLoading(true);
    try {
      const apiReportType = getReportTypeForAPI(reportType);
      const response = await getValidationStatusesForce(apiReportType, reportId);

      if (response.data) {
        const transformedStatuses = Object.entries(response.data).map(([value, [label, color]]) => ({
          value,
          label: normalizeStatusLabel(value, label),
          color
        }));

        setStatusOptions(transformedStatuses);
        return transformedStatuses;
      } else {
        setStatusOptions([]);
        return [];
      }
    } catch (error) {
      console.error('Error fetching validation statuses:', error);
      toast.error('Error loading validation statuses');
      setStatusOptions([]);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  const updateValidationStatus = useCallback(async (reportType, reportId, status) => {
    setLoading(true);
    try {
      const apiReportType = getReportTypeForAPI(reportType);
      const response = await updateValidationStatusForce(apiReportType, reportId, status);

      toast.success('Status updated successfully');
      return response.data;
    } catch (error) {
      console.error('Error updating validation status:', error);
      toast.error('Error updating validation status');
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    loading,
    statusOptions,
    fetchValidationStatuses,
    updateValidationStatus,
    setStatusOptions
  };
};